import React, { useEffect, useState } from 'react';
import { MessageCircle, Phone, Inbox } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { useOfferings } from '../../components/OfferingsProvider';
import { centerToast } from '../../components/CenterToast';
import { fetchDirectusUpdates } from '../../lib/updates';
import {
  MTFA_CONTACTS,
  contactKeyFor,
  waLinkForContact,
  telLinkForContact,
} from '../../lib/mtfaContacts';
import PushOptIn from '../../components/PushOptIn';

/**
 * Placeholder offers until there is a real offers source. These are illustrative
 * copy only — nothing here is redeemable, so every card taps through to a
 * "coming soon" toast rather than a destination or a claim flow.
 */
const PLACEHOLDER_OFFERS = [
  {
    id: 'ulum-course-fees',
    title: 'Ulum Ihsan course fees',
    subtitle: 'Early-bird rate on the next intake',
    flash: '20% OFF',
    image: '/images/ulum-ihsan-dua-surah-hero.jpeg'
  },
  {
    id: 'dialysis-transport',
    title: 'Dialysis transport credit',
    subtitle: 'For members on the treatment programme',
    flash: '$15 OFF',
    image: '/images/category-dialysis.png'
  },
  {
    id: 'volunteer-double-points',
    title: 'Double points weekend',
    subtitle: 'Ihsan Points on any volunteer shift',
    flash: '2X PTS',
    image: '/images/volunteer-hub-hero.jpeg'
  },
  {
    id: 'matched-giving',
    title: 'Matched giving campaign',
    subtitle: 'Every dollar matched by our donors',
    flash: '1-FOR-1',
    image: '/images/hero-slide-1-community.jpeg'
  }
];

export default function Moments({ onOpenExploreItem, onNavigate }) {
  const {
    learnHubItems,
    volunteerHubItems,
    givingOptions,
    serviceHubItems,
    dialysisCentres,
    casketPackages
  } = useOfferings();

  // Loading states:
  //   'loading'    initial fetch in flight
  //   'live'       Directus returned rows (>=1) — render them
  //   'empty'      Directus returned successfully with 0 published rows
  //   'unavailable' Directus down / env off — show honest empty, NOT stale mocks
  // The important launch-hygiene rule: never render mocks in prod. A mock
  // "Ramadan appeal" that isn't a real appeal is worse than an empty state.
  const [updates, setUpdates] = useState([]);
  const [state, setState] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const live = await fetchDirectusUpdates({ role: 'member' });
      if (cancelled) return;
      if (!live) {
        setState('unavailable');
        return;
      }
      if (!live.rows || live.rows.length === 0) {
        setState('empty');
        return;
      }
      setUpdates(live.rows);
      setState('live');
    })();
    return () => { cancelled = true; };
  }, []);

  const COLLECTIONS = {
    learn: learnHubItems,
    volunteer: volunteerHubItems,
    give: givingOptions,
    services: serviceHubItems,
    dialysis: dialysisCentres,
    casket: casketPackages
  };

  const resolveUpdateTarget = (u) => {
    const items = COLLECTIONS[u.linkType];
    const item = items?.find((i) => i.id === u.linkId);
    if (!item) return null;
    return { type: u.linkType, item };
  };

  const openUpdate = (u) => {
    const target = resolveUpdateTarget(u);
    if (target) {
      onOpenExploreItem?.(target);
      return;
    }
    if (u.externalUrl) {
      if (typeof window !== 'undefined') {
        window.open(u.externalUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }
    centerToast(u.title, '🔔');
  };

  const contactForUpdate = (u) => {
    // Prefer explicit service_contact on the update row; otherwise infer from
    // the linked item's id so a card that points at Ihsan Casket gets the
    // Ihsan Casket number, not the HQ number.
    if (u.serviceContact && MTFA_CONTACTS[u.serviceContact]) {
      return { key: u.serviceContact, ...MTFA_CONTACTS[u.serviceContact] };
    }
    const target = resolveUpdateTarget(u);
    if (target?.item?.id) {
      const key = contactKeyFor(target.item);
      return { key, ...MTFA_CONTACTS[key] };
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-background page-fade-in" data-testid="moments-page" data-updates-state={state}>
      <div className="px-4 pt-4 pb-1 desktop-shell" data-testid="moments-offers">
        <h2 className="text-base md:text-lg font-bold text-[#0F172A] mb-3">Offers for you</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {PLACEHOLDER_OFFERS.map((offer) => (
            <button
              key={offer.id}
              type="button"
              data-testid={`moments-offer-${offer.id}`}
              onClick={() => centerToast('Offers are coming soon', '🎁')}
              className="relative text-left rounded-2xl overflow-hidden bg-card border shadow card-hover"
            >
              <img src={offer.image} alt="" className="w-full h-24 md:h-36 object-cover" />
              <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-orange-600 text-white text-[10px] font-extrabold tracking-wide shadow-sm">
                {offer.flash}
              </span>
              <div className="p-2.5">
                <h3 className="text-sm font-bold leading-tight text-[#0F172A] line-clamp-2">{offer.title}</h3>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug line-clamp-2">{offer.subtitle}</p>
                <span className="inline-flex mt-1.5 items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold">
                  Coming soon
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <PushOptIn />

      <div className="px-4 pt-4 desktop-shell">
        <h2 className="text-base md:text-lg font-bold text-[#0F172A]">Community news</h2>
      </div>

      {state === 'loading' && (
        <div className="px-4 pt-3 pb-6 desktop-shell" data-testid="moments-news-loading">
          <div className="grid md:grid-cols-2 gap-3 md:gap-4">
            {[0, 1].map((i) => (
              <Card key={i} className="overflow-hidden">
                <CardContent className="p-3 flex gap-3">
                  <div className="w-14 h-14 rounded-lg bg-muted animate-pulse flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3.5 w-4/5 rounded bg-muted animate-pulse" />
                    <div className="h-3 w-full rounded bg-muted animate-pulse" />
                    <div className="h-3 w-2/3 rounded bg-muted animate-pulse" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {(state === 'empty' || state === 'unavailable') && (
        <div className="px-4 pt-3 pb-6 desktop-shell">
          <Card
            className="border-dashed"
            data-testid={state === 'empty' ? 'moments-news-empty' : 'moments-news-unavailable'}
          >
            <CardContent className="p-6 flex flex-col items-center text-center gap-2">
              <Inbox className="h-8 w-8 text-muted-foreground" aria-hidden />
              <h3 className="text-sm font-bold text-[#0F172A]">
                {state === 'empty'
                  ? 'No community news yet'
                  : 'Community news is offline right now'}
              </h3>
              <p className="text-xs text-muted-foreground max-w-sm">
                {state === 'empty'
                  ? 'Check back soon — MTFA will post announcements about upcoming programmes, appeals, and community moments here.'
                  : 'We could not reach the newsroom. Please refresh in a moment — nothing has changed with your account.'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="px-4 flex flex-col md:grid md:grid-cols-2 gap-3 md:gap-4 pt-3 pb-6 desktop-shell">
        {state === 'live' && updates.map((u) => {
          const contact = contactForUpdate(u);
          const waHref = contact
            ? waLinkForContact(contact.key, { serviceName: u.title })
            : null;
          const telHref = contact ? telLinkForContact(contact.key) : null;
          return (
            <Card
              key={u.id}
              data-testid={`moments-news-${u.id}`}
              data-contact-key={contact?.key || ''}
              className="overflow-hidden card-hover"
            >
              <CardContent className="p-3 flex flex-col gap-2">
                <button
                  type="button"
                  data-testid={`moments-news-${u.id}-open`}
                  onClick={() => openUpdate(u)}
                  className="flex gap-3 text-left"
                >
                  <img src={u.image} alt={u.title} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-sm font-bold leading-tight truncate">{u.title}</h3>
                      <span className="text-[11px] text-muted-foreground shrink-0">{u.time}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug line-clamp-2">{u.body}</p>
                    <span className={`inline-flex mt-1.5 items-center px-2 py-0.5 rounded-full text-[10px] font-semibold text-white ${u.tagColor}`}>
                      {u.tag}
                    </span>
                  </div>
                </button>
                {waHref && (
                  <div className="flex items-center gap-2 pt-1 border-t border-border/50">
                    <a
                      href={waHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`moments-news-${u.id}-whatsapp`}
                      data-wa-number={contact.phoneE164}
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 hover:text-emerald-800"
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                      WhatsApp {contact.label}
                    </a>
                    <a
                      href={telHref}
                      data-testid={`moments-news-${u.id}-call`}
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#0F172A] ml-auto"
                    >
                      <Phone className="h-3.5 w-3.5" />
                      {contact.phoneDisplay}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
